import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';
import {
  Lock,
  Unlock,
  Settings,
  User,
  Bell,
  Shield,
  Sparkles,
  CheckCircle,
  Globe,
  Zap,
  Save,
  Loader2,
  MapPin,
  Users,
  Calendar,
  MessageSquare,
  BarChart3,
  Building2,
  TreePine,
  DollarSign,
  Compass,
  Image,
  Link2,
  ExternalLink,
  MonitorCheck,
  ArrowRight,
} from 'lucide-react';
import { updateUnlockedSections, updateCompletedSetupSteps } from '@/utils/businessAppApi';
import { useBusinessSetup } from '@/contexts/BusinessSetupContext';
import { useTranslation } from 'react-i18next';
import { config } from '@/config';
import { BusinessImageUpload } from './BusinessImageUpload';
import { cn } from '@/lib/utils';

interface BusinessProfile {
  businessId: string;
  businessName: string;
  businessType: 'sanctuary' | 'reserve' | 'park' | 'zoo';
  contactEmail: string;
  phoneNumber: string;
  address: string;
  description: string;
  website: string;
  socialMedia: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
  bookingMode?: 'platform' | 'redirect';
  bookingUrl?: string;
  whatsappNumber?: string;
  images?: {
    logo?: string;
    cover?: string;
    gallery: string[];
  };
}

const getFeatureDescription = (key: string): string => {
  const descriptions: { [key: string]: string } = {
    analytics: 'Track your business performance with detailed analytics and insights',
    activities: 'Manage and create new wildlife experiences and tours',
    bookings: 'Handle reservations and manage your booking calendar',
    calendar: 'View and organize your activities in a calendar view',
    communication: 'Engage with customers through messaging and notifications',
    staff: 'Manage your team members and their permissions',
    mapping: 'Create and manage trail maps and points of interest',
    livestock: 'Track and manage wildlife in your care',
    funding: 'Access funding opportunities and manage finances',
    'custom-adventures': 'Create and manage custom adventure experiences with dynamic pricing and booking requests'
  };
  return descriptions[key] || 'Feature description not available';
};

const getFeatureIcon = (key: string) => {
  const icons: { [key: string]: any } = {
    analytics: BarChart3,
    activities: TreePine,
    bookings: Calendar,
    calendar: Calendar,
    communication: MessageSquare,
    staff: Users,
    mapping: MapPin,
    livestock: Building2,
    funding: DollarSign,
    'custom-adventures': Compass
  };
  return icons[key] || Sparkles;
};

export function SettingsDashboard() {
  const { unlockedSections, refreshProgress } = useBusinessSetup();
  const { t } = useTranslation('business', { keyPrefix: 'settings' });
  const { t: tc } = useTranslation('common');
  
  const [activeTab, setActiveTab] = useState(() => {
    // Check if we were navigated here from the homepage setup
    const state = window.history.state;
    const section = state?.usr?.section || '';
    return section || 'profile';
  });

  // Get businessId from localStorage
  const [businessId, setBusinessId] = useState<string>(() => {
    try {
      const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
      const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
      const auth = businessAuth.token ? businessAuth : publicAuth;
      return auth.user?.businessId || '';
    } catch (error) {
      console.error('Error getting businessId:', error);
      return '';
    }
  });

  const [profile, setProfile] = useState<BusinessProfile>({
    businessId,
    businessName: 'Wildlife Sanctuary Demo',
    businessType: 'sanctuary',
    contactEmail: 'contact@demo.com',
    phoneNumber: '+1234567890',
    address: '123 Nature Way',
    description: '',
    website: '',
    socialMedia: {},
    images: {
      gallery: []
    }
  });

  const [selectedFeatures, setSelectedFeatures] = useState<{ [key: string]: boolean }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Booking settings state
  const [bookingMode, setBookingMode] = useState<'platform' | 'redirect'>('platform');
  const [bookingUrl, setBookingUrl] = useState('');
  const [isSavingBooking, setIsSavingBooking] = useState(false);

  // Password state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError]     = useState('');
  const [isSavingPw, setIsSavingPw] = useState(false);

  // Check if we need to mark a step as complete from the setup flow
  useEffect(() => {
    const checkAndCompleteSetupStep = async () => {
      const currentStep = localStorage.getItem('currentSetupStep');
      if (currentStep) {
        try {
          // Get completed steps
          const response = await fetch(`${config.apiUrl}/business/completed-setup-steps`, {
            headers: {
              'Authorization': `Bearer ${(() => {
                const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
                const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
                const auth = businessAuth.token ? businessAuth : publicAuth;
                return auth.token;
              })()}`
            }
          });
          
          let completedSteps: string[] = [];
          if (response.ok) {
            const data = await response.json();
            completedSteps = data.data?.steps || [];
          } else {
            // Fallback to localStorage
            const savedSteps = localStorage.getItem('completedSetupSteps');
            if (savedSteps) {
              completedSteps = JSON.parse(savedSteps);
            }
          }
          
          // Only add if not already completed
          if (!completedSteps.includes(currentStep)) {
            completedSteps.push(currentStep);
            
            // Save to localStorage as fallback
            localStorage.setItem('completedSetupSteps', JSON.stringify(completedSteps));
            
            // Update on server
            try {
              await updateCompletedSetupSteps(completedSteps);
              
              // Refresh progress in context
              await refreshProgress();
              
            } catch (error) {
              console.error('Error updating completed setup steps:', error);
            }
            
            toast.success(`Setup step completed!`, {
              description: 'Your setup progress has been updated.',
            });
          }
          
          // Clear current step
          localStorage.removeItem('currentSetupStep');
        } catch (error) {
          console.error('Error handling setup step completion:', error);
        }
      }
    };
    
    checkAndCompleteSetupStep();
  }, [refreshProgress]);

  // Fetch business profile
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      setIsLoadingProfile(true);
      try {
        const response = await fetch(`${config.apiUrl}/business/profile`, {
          headers: {
            'Authorization': `Bearer ${(() => {
              const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
              const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
              const auth = businessAuth.token ? businessAuth : publicAuth;
              return auth.token;
            })()}`
          }
        });
        if (response.ok) {
          const d = await response.json();
          const biz = d.data;
          // Map API field names to local state field names
          setProfile(prev => ({
            ...prev,
            businessId:   biz._id             ?? prev.businessId,
            businessName: biz.name             ?? '',
            businessType: biz.type             ?? 'sanctuary',
            contactEmail: biz.contact?.email   ?? '',
            phoneNumber:  biz.contact?.phone   ?? '',
            description:  biz.description     ?? '',
            website:      biz.contact?.website ?? '',
            images:       biz.images          ?? { gallery: [] },
          }));
          setBookingMode(biz.contact?.bookingMode || 'platform');
          setBookingUrl(biz.contact?.bookingUrl || '');
        }
      } catch (error) {
        console.error('Error fetching business profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    fetchBusinessProfile();
  }, []);

  // Use the unlocked sections from context
  useEffect(() => {
    if (Object.keys(unlockedSections).length > 0) {
      setSelectedFeatures(unlockedSections);
    }
  }, [unlockedSections]);

  const getToken = () => {
    try {
      const ba = JSON.parse(localStorage.getItem('businessAuth') || '{}');
      const pa = JSON.parse(localStorage.getItem('auth') || '{}');
      return (ba.token ? ba : pa).token || '';
    } catch { return ''; }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSavingProfile(true);
    try {
      const res = await fetch(`${config.apiUrl}/business/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          name: profile.businessName,
          type: profile.businessType,
          description: profile.description,
          contact: {
            email:   profile.contactEmail,
            phone:   profile.phoneNumber,
            website: profile.website,
          },
        }),
      });
      const d = await res.json();
      if (d.success) toast.success('Profile saved');
      else toast.error(d.error || 'Failed to save');
    } catch {
      toast.error('Failed to save');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    if (newPw !== confirmPw) { setPwError('New passwords do not match'); return; }
    if (newPw.length < 8)    { setPwError('Password must be at least 8 characters'); return; }
    setIsSavingPw(true);
    try {
      const res = await fetch(`${config.apiUrl}/users/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      const d = await res.json();
      if (d.success) {
        toast.success('Password updated');
        setCurrentPw(''); setNewPw(''); setConfirmPw('');
      } else {
        setPwError(d.error || 'Failed to update password');
      }
    } catch {
      setPwError('Failed to update password');
    } finally {
      setIsSavingPw(false);
    }
  };

  const handleFeatureToggle = (key: string) => {
    setSelectedFeatures(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleApplyChanges = async () => {
    setIsSaving(true);
    try {
      await updateUnlockedSections(selectedFeatures);
      
      // After updating, refresh context
      await refreshProgress();
      
      // Dispatch custom event to update sidebar
      const event = new CustomEvent('featuresUpdated', { 
        detail: selectedFeatures 
      });
      window.dispatchEvent(event);

      toast.success('Features updated successfully!', {
        description: 'Your dashboard has been updated with the selected features.',
      });
    } catch (error) {
      console.error('Error updating features:', error);
      toast.error('Failed to update features', {
        description: 'Please try again or contact support if the problem persists.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBookingSave = async () => {
    if (bookingMode === 'redirect' && !bookingUrl.trim()) {
      toast.error('Please enter your booking URL');
      return;
    }
    if (bookingMode === 'redirect') {
      try { new URL(bookingUrl.trim()); } catch {
        toast.error('Please enter a valid URL (e.g. https://yoursite.com/book)');
        return;
      }
    }
    setIsSavingBooking(true);
    try {
      const token = (() => {
        const ba = JSON.parse(localStorage.getItem('businessAuth') || '{}');
        const pa = JSON.parse(localStorage.getItem('auth') || '{}');
        return (ba.token ? ba : pa).token || '';
      })();
      const res = await fetch(`${config.apiUrl}/business/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          bookingMode,
          bookingUrl: bookingMode === 'redirect' ? bookingUrl.trim() : '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Booking settings saved!', { description: 'Your public profile has been updated.' });
      } else {
        toast.error('Could not save. Please try again.');
      }
    } catch {
      toast.error('Could not save. Please try again.');
    } finally {
      setIsSavingBooking(false);
    }
  };

  const handleImagesUpdate = (updatedImages: BusinessProfile['images']) => {
    setProfile(prev => ({
      ...prev,
      images: updatedImages
    }));
  };

  const TAB_CLASS = cn(
    "relative bg-transparent rounded-none h-auto px-2.5 sm:px-4 py-3 text-sm font-medium whitespace-nowrap",
    "text-slate-500 hover:text-slate-900 transition-colors duration-150",
    "border-b-2 border-transparent -mb-px shadow-none",
    "data-[state=active]:text-primary data-[state=active]:border-primary",
    "data-[state=active]:bg-transparent data-[state=active]:shadow-none"
  );

  return (
    <div className="space-y-0">
      <div className="pb-4 pt-1">
        <h1 className="text-lg sm:text-xl font-bold text-slate-900">Settings</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        {/* Underline-style tab bar — scrollable on mobile */}
        <div className="border-b border-slate-200 -mx-4 sm:mx-0 px-4 sm:px-0">
          <TabsList className="bg-transparent p-0 h-auto rounded-none gap-0 inline-flex w-full">
            <TabsTrigger value="profile"      className={TAB_CLASS}>Profile</TabsTrigger>
            <TabsTrigger value="booking"      className={TAB_CLASS}>Booking</TabsTrigger>
            <TabsTrigger value="images"       className={TAB_CLASS}>Images</TabsTrigger>
            <TabsTrigger value="security"     className={TAB_CLASS}>Security</TabsTrigger>
            <TabsTrigger value="subscription" className={TAB_CLASS}>Features</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profile">
          <div className="space-y-6">
            <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
              <CardContent className="pt-6">
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="businessName" className="text-sm font-medium text-slate-700">{t('businessName')}</Label>
                      <Input
                        id="businessName"
                        value={profile.businessName}
                        onChange={e => setProfile(p => ({ ...p, businessName: e.target.value }))}
                        className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessType" className="text-sm font-medium text-slate-700">{t('businessType')}</Label>
                      <Select
                        value={profile.businessType}
                        onValueChange={value => setProfile(p => ({ ...p, businessType: value as any }))}
                      >
                        <SelectTrigger className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sanctuary">{t('wildlifeSanctuary')}</SelectItem>
                          <SelectItem value="reserve">{t('natureReserve')}</SelectItem>
                          <SelectItem value="park">{t('nationalPark')}</SelectItem>
                          <SelectItem value="zoo">{t('zoo')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contactEmail" className="text-sm font-medium text-slate-700">{t('contactEmail')}</Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        value={profile.contactEmail}
                        onChange={e => setProfile(p => ({ ...p, contactEmail: e.target.value }))}
                        className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phoneNumber" className="text-sm font-medium text-slate-700">{t('phoneNumber')}</Label>
                      <Input
                        id="phoneNumber"
                        type="tel"
                        value={profile.phoneNumber}
                        onChange={e => setProfile(p => ({ ...p, phoneNumber: e.target.value }))}
                        className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-sm font-medium text-slate-700">{t('businessDescription')}</Label>
                    <textarea
                      id="description"
                      className="w-full min-h-[100px] p-3 border border-slate-200/80 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none text-sm"
                      value={profile.description}
                      onChange={e => setProfile(p => ({ ...p, description: e.target.value }))}
                      placeholder={t('describeYourBusiness')}
                    />
                  </div>

                  <div className="flex justify-end pt-4 border-t border-slate-200/60">
                    <Button
                      type="submit"
                      disabled={isSavingProfile}
                      className="bg-primary hover:bg-[#245225] px-6 flex items-center gap-2"
                    >
                      {isSavingProfile ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : <><Save className="w-4 h-4" />{tc('saveChanges')}</>}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

          </div>
        </TabsContent>

        <TabsContent value="images">
          <div className="space-y-5">

            {/* Compact status row */}
            <div className="flex gap-4 text-sm">
              {[
                { label: 'Logo',    done: !!profile.images?.logo },
                { label: 'Cover',   done: !!profile.images?.cover },
                { label: `Gallery (${profile.images?.gallery?.length ?? 0})`, done: (profile.images?.gallery?.length ?? 0) > 0 },
              ].map(({ label, done }) => (
                <span key={label} className={cn('flex items-center gap-1 font-medium', done ? 'text-emerald-600' : 'text-slate-400')}>
                  {done ? '✓' : '○'} {label}
                </span>
              ))}
            </div>

            {/* Upload */}
            {businessId && (
              <BusinessImageUpload
                businessId={businessId}
                images={profile.images || { gallery: [] }}
                onImagesUpdate={handleImagesUpdate}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="subscription">
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
            <CardContent className="pt-4 pb-2">
              <div className="divide-y divide-slate-100">
                {Object.entries(selectedFeatures).map(([key, isEnabled]) => {
                  if (key === 'home' || key === 'settings') return null;
                  const label = key === 'custom-adventures' ? 'Custom Adventures'
                    : key.charAt(0).toUpperCase() + key.slice(1);
                  return (
                    <div key={key} className="flex items-center justify-between py-3">
                      <span className={`text-sm font-medium ${isEnabled ? 'text-slate-800' : 'text-slate-400'}`}>
                        {label}
                      </span>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => handleFeatureToggle(key)}
                        disabled={isSaving}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="pt-4 border-t border-slate-100 flex justify-end">
                <Button
                  onClick={handleApplyChanges}
                  disabled={isSaving}
                  className="bg-primary hover:bg-[#245225] px-5 flex items-center gap-2"
                >
                  {isSaving ? <><Loader2 className="w-4 h-4 animate-spin" />{t('applyingChanges')}</> : <><Save className="w-4 h-4" />{t('applyChanges')}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white hover:shadow-lg hover:shadow-slate-900/8 transition-shadow duration-200">
            <CardHeader className="pb-4 border-b border-slate-100/80">
              <CardTitle className="flex items-center gap-2 text-xl">
                <div className="w-8 h-8 rounded-lg bg-amber-100 border border-amber-200/50 flex items-center justify-center shadow-sm">
                  <Bell className="w-4 h-4 text-amber-600" />
                </div>
                {t('notificationSettings')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <Bell className="w-6 h-6 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Coming Soon</h3>
                  <p className="text-slate-600 max-w-sm">
                    Notification settings will be available in a future update.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
            <CardContent className="pt-6 space-y-6">
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700">Change Password</h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="currentPassword" className="text-sm font-medium text-slate-700">Current password</Label>
                    <Input id="currentPassword" type="password" value={currentPw}
                      onChange={e => setCurrentPw(e.target.value)}
                      className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm" required />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="newPassword" className="text-sm font-medium text-slate-700">New password</Label>
                      <Input id="newPassword" type="password" value={newPw}
                        onChange={e => setNewPw(e.target.value)}
                        className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">Confirm new password</Label>
                      <Input id="confirmPassword" type="password" value={confirmPw}
                        onChange={e => setConfirmPw(e.target.value)}
                        className="focus:ring-emerald-500 focus:border-emerald-500 border-slate-200/80 shadow-sm" required />
                    </div>
                  </div>
                  {pwError && <p className="text-sm text-red-600 font-medium">{pwError}</p>}
                </div>
                <Button type="submit" disabled={isSavingPw}
                  className="bg-primary hover:bg-[#245225] flex items-center gap-2">
                  {isSavingPw ? <><Loader2 className="w-4 h-4 animate-spin" />Updating…</> : 'Update Password'}
                </Button>
              </form>

              <div className="pt-4 border-t border-slate-200/60">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Two-Factor Authentication</h3>
                <div className="flex items-center justify-between p-4 border border-slate-100 rounded-lg bg-slate-50/50">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Not enabled</p>
                    <p className="text-xs text-slate-400 mt-0.5">Coming soon</p>
                  </div>
                  <Button variant="outline" disabled className="text-slate-400 border-slate-200 text-sm">Enable 2FA</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="booking" className="space-y-6">
          <Card className="border border-slate-200/60 shadow-md shadow-slate-900/5 bg-white">
            <CardContent className="pt-6 space-y-6">

              {/* Mode selector — two big cards */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Booking method</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  {/* Platform card */}
                  <button
                    type="button"
                    onClick={() => setBookingMode('platform')}
                    className={cn(
                      'text-left rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-md focus:outline-none',
                      bookingMode === 'platform'
                        ? 'border-emerald-500 bg-emerald-50 shadow-md shadow-emerald-500/10'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                        bookingMode === 'platform' ? 'bg-emerald-100' : 'bg-slate-100'
                      )}>
                        <MonitorCheck className={cn('w-5 h-5', bookingMode === 'platform' ? 'text-emerald-600' : 'text-slate-400')} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">Book via Wildy</p>
                          {bookingMode === 'platform' && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                          Customers book on Wildy. You manage availability and receive payment here.
                        </p>
                      </div>
                    </div>
                  </button>

                  {/* Redirect card */}
                  <button
                    type="button"
                    onClick={() => setBookingMode('redirect')}
                    className={cn(
                      'text-left rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-md focus:outline-none',
                      bookingMode === 'redirect'
                        ? 'border-blue-500 bg-blue-50 shadow-md shadow-blue-500/10'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        'w-10 h-10 rounded-lg flex items-center justify-center shrink-0',
                        bookingMode === 'redirect' ? 'bg-blue-100' : 'bg-slate-100'
                      )}>
                        <ExternalLink className={cn('w-5 h-5', bookingMode === 'redirect' ? 'text-blue-600' : 'text-slate-400')} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">Your own website</p>
                          {bookingMode === 'redirect' && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Active</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                          Customers click through to your own website. Wildy tracks every referral.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* URL input — only shown when redirect is selected */}
              {bookingMode === 'redirect' && (
                <div className="space-y-3 rounded-xl bg-blue-50/60 border border-blue-100 p-5 transition-all duration-200">
                  <div className="space-y-1.5">
                    <Label htmlFor="bookingUrl" className="text-sm font-medium text-slate-700">
                      Your booking URL
                    </Label>
                    <p className="text-xs text-slate-500">
                      The page where customers can book on your website
                    </p>
                    <Input
                      id="bookingUrl"
                      type="url"
                      placeholder="https://yourwebsite.com/book"
                      value={bookingUrl}
                      onChange={e => setBookingUrl(e.target.value)}
                      className="bg-white border-blue-200 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"
                    />
                  </div>

                  {/* Live preview */}
                  {bookingUrl.trim() && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-white border border-blue-200/60 shadow-sm">
                      <ArrowRight className="w-4 h-4 text-blue-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-slate-500 mb-0.5">Customers will be sent to:</p>
                        <p className="text-sm font-mono text-blue-700 truncate">
                          {(() => {
                            try {
                              const u = new URL(bookingUrl.trim());
                              u.searchParams.set('ref', 'wildy');
                              return u.toString();
                            } catch {
                              return bookingUrl.trim() + '?ref=wildy';
                            }
                          })()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Save */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-200/60">
                <Button
                  onClick={handleBookingSave}
                  disabled={isSavingBooking}
                  className="bg-emerald-600 hover:bg-emerald-700 px-6 shadow-md hover:shadow-lg transition-shadow flex items-center gap-2"
                >
                  {isSavingBooking ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Saving…</>
                  ) : (
                    <><Save className="w-4 h-4" />Save Booking Settings</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
} 